import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { verifyEmailAddress } from '@/api/auth/authApi';
import { BrandLogo } from '@/components/common/BrandLogo';
import { Icon } from '@/components/common/Icon';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';

type VerificationState = 'verifying' | 'confirmed' | 'invalid';

export function VerifyEmailScreen() {
  const { t } = useTranslation('auth');
  const { status, refreshCurrentUser } = useAuth();
  const [searchParameters] = useSearchParams();
  const tokenParameter = searchParameters.get('token');
  const resolvedToken = tokenParameter === null ? '' : tokenParameter;
  const [verificationState, setVerificationState] = useState<VerificationState>('verifying');
  const hasStartedVerification = useRef(false);

  useEffect(
    function confirmEmailAddress() {
      if (hasStartedVerification.current === true) {
        return;
      }
      hasStartedVerification.current = true;
      if (resolvedToken.length === 0) {
        setVerificationState('invalid');
        return;
      }
      verifyEmailAddress(resolvedToken)
        .then(function markConfirmed() {
          setVerificationState('confirmed');
        })
        .catch(function markInvalid() {
          setVerificationState('invalid');
        });
    },
    [resolvedToken]
  );

  useEffect(
    function refreshSignedInUserAfterConfirmation() {
      if (verificationState === 'confirmed' && status === 'authenticated') {
        void refreshCurrentUser();
      }
    },
    [verificationState, status, refreshCurrentUser]
  );

  return (
    <div className="min-h-screen bg-zoooffwhite flex items-center justify-center p-4">
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-[2.5rem] shadow-xl overflow-hidden">
        <div className="p-8 md:p-10">
          <div className="flex justify-center mb-7">
            <BrandLogo className="h-10 w-auto object-contain" />
          </div>

          {verificationState === 'verifying' ? (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <LoadingSpinner />
              </div>
              <h1 className="text-xl font-bold text-zooindigo">
                {t('verifyEmailScreen.verifyingTitle')}
              </h1>
              <p className="text-sm text-zoomuted mt-2">
                {t('verifyEmailScreen.verifyingSubtitle')}
              </p>
            </div>
          ) : verificationState === 'confirmed' ? (
            <div className="text-center">
              <div className="w-14 h-14 rounded-2xl bg-zoosuccessbg flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" className="text-xl text-zoosuccesstext" />
              </div>
              <h1 className="text-xl font-bold text-zooindigo">
                {t('verifyEmailScreen.successTitle')}
              </h1>
              <p className="text-sm text-zoomuted mt-2">{t('verifyEmailScreen.successSubtitle')}</p>
              <Button asChild size="large" className="mt-6 w-full">
                <Link to="/">{t('verifyEmailScreen.continue')}</Link>
              </Button>
            </div>
          ) : (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-2xl bg-zooerrorbg flex items-center justify-center mx-auto mb-4">
                <Icon name="AlertCircle" className="text-xl text-zooerrortext" />
              </div>
              <h1 className="text-xl font-bold text-zooindigo">
                {t('verifyEmailScreen.invalidTitle')}
              </h1>
              <p className="text-sm text-zoomuted mt-2">
                {t('verifyEmailScreen.invalidDescription')}
              </p>
              <Button asChild size="large" className="mt-6 w-full">
                <Link to="/">{t('verifyEmailScreen.continue')}</Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
